import * as React from "react";
import { useCallback, useEffect } from "react";
import { Button, Gapped, Modal, Toast } from "react-ui";
import { useDispatch } from "react-redux";

import { hideModalActionCreator } from "../../FormModalAction";
import {
    CombinatorType,
    CombineFufResult,
    FufCombineMethod,
} from "../../../../../../../Common/CandyApiClient/Types/DraftActions/CombineFufResult";
import { ModalStateInfo } from "../../IModalState";
import {
    ExcelsToPartialFufsResponse,
    ServerErrorResponse,
} from "../../../../../../../Common/CandyApiClient/Types/DraftActions/FileProperties";
import { isNotNullOrEmpty, noop, reject } from "../../../../../../../Common/TypingUtils";
import { useAsyncEffect } from "../../../../../../../Common/UseAsyncEffect";
import {
    extractFileExtension,
    FileProps,
    FilePropsWithTypeId,
    PatchFufSteps,
    TableType,
    UploadFileStatuses,
} from "../Utils/Types";
import { ModalText, StepPage } from "../Utils/Styles/Modals.styles";
import {
    EXCEL_UPLOAD_CLICK_CLOSE,
    EXCEL_UPLOAD_FILE,
    EXCEL_UPLOAD_FILES,
    EXCEL_UPLOAD_SELECT_ADD,
    EXCEL_UPLOAD_SELECT_REPLACE,
    PATCH_FUF_MODAL_TRANSFER_TYPE,
} from "../../../../../Controls/metricsAction";
import { useMetrics } from "../../../../../Controls/Internals/Metrics/MetricsProviderContext";
import { useUploadFile } from "../Utils/UploadFileRedisign/UseUploadFile";
import {
    getAcceptFilesError,
    getStepHeaderContent,
    getTableTypeHeaderContent,
    invalidFileErrorText,
} from "../Utils/TextContent";
import { CombineMethodPicker } from "../Utils/CombineMethodPicker/CombineMethodPicker";
import { ModalLoader } from "../Utils/ModalLoader/ModalLoader";
import { smallModalWidth, wideModalWidth } from "../Utils/Styles/ModalWidth";
import { FileException, Underline } from "../Utils/UploadFileRedisign/UploadFile.styles";
import {
    CheckPropertyErrors,
    ICombineByTransfers,
    ICombineByTransfersSort,
    TransferTypes,
} from "../UploadSection/Types";
import { UploadFileException } from "../Utils/UploadFileRedisign/UploadFileException";
import { CustomFile } from "../../../../../Helpers/file";
import { UploadedFileInfo } from "../../../../ActionCreators/FufUploadActionCreator";
import { delay } from "../../../../utils";
import { MergeNotice } from "../UploadSection/MergeNotice/MergeNotice";
import { useFormObject } from "../../../../../Controls/FormObjectProvider";

export interface UploadExcelInTableModalProps {
    modalSettings?: ModalStateInfo;
    currentGfv: string;
    reload: () => Promise<void>;
    importXmls: (
        driveUris: string[],
        combineMethod: FufCombineMethod,
        combinatorType: CombinatorType,
        isImportByFuf: boolean,
        args?: string[]
    ) => Promise<CombineFufResult | undefined>;
    excelsToPartialFufs: (
        gfv: string,
        pathToTable: string,
        driveUris: string[]
    ) => Promise<ExcelsToPartialFufsResponse | undefined>;
    combineMultipleWithTransfer: (
        files: ICombineByTransfers[],
        abortSignal?: AbortSignal
    ) => Promise<ServerErrorResponse | undefined>;
    getFileProperties: (
        file: CustomFile | undefined,
        ignoreSizeCheck?: boolean,
        skipUploadFile?: boolean
    ) => Promise<UploadedFileInfo>;
}

export interface ValidDriveURL {
    id: string;
    excelURl: string;
    xmlURL: string;
    typeId?: string;
}

export const UploadExcelInTableModal = ({
    modalSettings,
    reload,
    currentGfv,
    importXmls,
    excelsToPartialFufs,
    combineMultipleWithTransfer,
    getFileProperties,
}: UploadExcelInTableModalProps) => {
    const dispatch = useDispatch();
    const track = useMetrics();
    const form = useFormObject();

    const abortController = React.useRef(new AbortController());
    const [loading, setLoading] = React.useState(false);
    const [step, setStep] = React.useState<PatchFufSteps>(PatchFufSteps.SelectCombineFufMethod);
    const [modalHeader, setModalHeader] = React.useState<React.ReactNode>(
        <>Загрузка файл{modalSettings?.multiple ? "ов" : "а"}</>
    );
    const [fufCombineMethod, setFufCombineMethod] = React.useState<FufCombineMethod>(FufCombineMethod.AddToEnd);
    const memoizedFilter = useCallback(filterValidFiles, []);
    const memoizedCheckValidFile = useCallback(checkValidFile, []);
    const memoizedCheckValidFileWithTypeId = useCallback(checkValidFileWithTypeId, []);

    const { filesContainer, uploadedFiles, validDriveURLs, addUserFilesToDrive } = useUploadFile<ValidDriveURL>({
        filterValidDriveUrl: memoizedFilter,
        checkValidFile: modalSettings?.getTypeId ? memoizedCheckValidFileWithTypeId : memoizedCheckValidFile,
        multiple: modalSettings?.multiple ?? true,
    });

    function filterValidFiles(fileToRemove: FileProps, url: ValidDriveURL | ICombineByTransfersSort) {
        return url.id !== fileToRemove.id;
    }

    React.useEffect(() => {
        setModalHeader(getStepHeaderContent(fufCombineMethod, modalSettings?.multiple));
    }, [step]);

    useAsyncEffect(async () => {
        if (modalSettings?.currentInstancesCount === 0) {
            setStep(PatchFufSteps.SelectFiles);
            setFufCombineMethod(FufCombineMethod.AddToEnd);
            await addUserFilesToDrive(modalSettings?.multiple);
        } else {
            setStep(PatchFufSteps.SelectCombineFufMethod);
        }

        await form.changeSet.send();
    }, []);

    useEffect(() => {
        setModalHeader(getTableTypeHeaderContent(modalSettings?.tableType));
    }, []);

    const onClose = () => {
        track(EXCEL_UPLOAD_CLICK_CLOSE, { path: modalSettings?.pathToTable });
        dispatch(hideModalActionCreator());
    };

    const onDownloadFileHandler = async () => {
        const invalidFilesCount = uploadedFiles.length - validDriveURLs.length;
        const invalidExcelFilesCount = uploadedFiles
            .map(item => item.errorMessage?.props.children === invalidFileErrorText)
            .filter(item => item).length;

        track(EXCEL_UPLOAD_FILES, {
            path: modalSettings?.pathToTable,
            filesCount: uploadedFiles.length,
            validFilesCount: validDriveURLs.length,
            invalidFilesCount: invalidFilesCount,
            invalidExcelFilesCount: invalidExcelFilesCount,
            invalidNotExcelFilesCount: invalidFilesCount - invalidExcelFilesCount,
        });
        const filesToUpload = extractValidFilesDriveUrls();
        if (filesToUpload.length && filesToUpload) {
            setStep(PatchFufSteps.LoadToForm);
            const isReplaceMethod =
                fufCombineMethod === FufCombineMethod.ReplaceThenMerge ||
                fufCombineMethod === FufCombineMethod.ReplaceThenAddToEnd;
            const combineMethod = isReplaceMethod
                ? fufCombineMethod
                : (modalSettings?.combineType ?? FufCombineMethod.AddToEnd);
            await uploadFilesFromExcel(modalSettings?.pathToTable ?? "", combineMethod, modalSettings?.isImportByFuf);
        }
    };

    async function refreshExcelUpload() {
        Toast.close();
        await onDownloadFileHandler();
    }

    async function checkValidFile(
        file: FileProps,
        setValidDriveURLs: React.Dispatch<React.SetStateAction<ValidDriveURL[]>>
    ): Promise<FileProps> {
        let errorMessageText: string = "";
        let properties: ExcelsToPartialFufsResponse | undefined;
        if (!modalSettings?.acceptFiles?.includes(extractFileExtension(file.uploadFile.name))) {
            errorMessageText = getAcceptFilesError(modalSettings?.acceptFiles);
        } else {
            if (file.id && file.driveUri) {
                properties = await excelsToPartialFufs(currentGfv, modalSettings?.pathToTable ?? "", [file.driveUri]);
                if (properties?.failed.length !== 0) {
                    errorMessageText = invalidFileErrorText;
                }
                if (properties?.parsed.length !== 0) {
                    const newValidURL: ValidDriveURL = {
                        id: file.id,
                        excelURl: file.driveUri,
                        xmlURL: properties?.parsed[0] ?? "",
                    };
                    setValidDriveURLs(value => [...value, newValidURL]);
                }
            }
        }
        const status = errorMessageText ? UploadFileStatuses.error : UploadFileStatuses.success;
        track(EXCEL_UPLOAD_FILE, {
            path: modalSettings?.pathToTable,
            uploadFile: file.uploadFile.name,
            size: file.uploadFile.size,
            status: status,
            errorMessage: errorMessageText,
        });
        return {
            id: file.id,
            uploadFile: file.uploadFile,
            status: status,
            errorMessage: <FileException>{errorMessageText}</FileException>,
            driveUri: file.driveUri,
        };
    }

    async function checkValidFileWithTypeId(
        file: FileProps,
        setValidDriveURLs: React.Dispatch<React.SetStateAction<ValidDriveURL[]>>
    ): Promise<FilePropsWithTypeId> {
        let errorMessageText: string = "";
        let properties: ExcelsToPartialFufsResponse | undefined;
        let excelToXmlUrl: string = "";
        const sectionInfo = modalSettings?.sectionInfo;
        if (!modalSettings?.acceptFiles?.includes(extractFileExtension(file.uploadFile.name))) {
            errorMessageText = getAcceptFilesError(modalSettings?.acceptFiles);
        } else {
            if (file.id && file.driveUri) {
                properties = await excelsToPartialFufs(currentGfv, modalSettings?.pathToTable ?? "", [file.driveUri]);
                if (properties?.failed.length !== 0) {
                    if (
                        properties?.failed[0]?.item2.includes("Размер файла") &&
                        properties?.failed[0]?.item2.includes("превышает предел")
                    ) {
                        errorMessageText = "Файл недоступен для загрузки: размер файла превышает ограничение.";
                    }
                    if (properties?.failed[0]?.item2.includes("Загружаемые данные не соответствуют шаблону")) {
                        errorMessageText = `В файле нет данных по подразделу ${sectionInfo?.name}.`;
                    } else {
                        errorMessageText = invalidFileErrorText;
                    }
                }
                if (properties?.parsed[0]) {
                    excelToXmlUrl = properties?.parsed[0];
                }
            }
        }

        const fileState: FilePropsWithTypeId = {
            id: file.id,
            uploadFile: file.uploadFile,
            status: UploadFileStatuses.error,
            driveUri: excelToXmlUrl,
            errorMessage: errorMessageText ? <FileException>{errorMessageText}</FileException> : undefined,
        };
        if (errorMessageText !== "" || excelToXmlUrl === "") {
            return fileState;
        }
        modalSettings?.getTransferIdByProperties ??
            reject(
                `getTransferIdByProperties is not defined. Check it in openModalForUploadSection.js in ${currentGfv} helpers`
            );

        const driveUriGuard = file.driveUri;

        if (file.id && driveUriGuard && !errorMessageText) {
            const fileProperties = await getFileProperties(
                {
                    name: file.id,
                    size: file.uploadFile.size,
                    contentUri: excelToXmlUrl,
                },
                false,
                true
            );

            const transferType: TransferTypes = modalSettings?.getTransferIdByProperties?.(
                fileProperties.fileProperties,
                sectionInfo?.gfv
            ) as TransferTypes;

            track(PATCH_FUF_MODAL_TRANSFER_TYPE, {
                uploadSection: sectionInfo?.gfv,
                transferType: transferType,
            });

            // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
            if (transferType === CheckPropertyErrors.NotSectionFound) {
                fileState.errorMessage = sectionInfo?.fileUploadNotSectionFoundErrorMessage ? (
                    <UploadFileException errorMessage={sectionInfo?.fileUploadNotSectionFoundErrorMessage} />
                ) : (
                    <FileException>
                        В файле нет данных по подразделу <Underline>{sectionInfo?.name}</Underline>
                    </FileException>
                );
                // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
            } else if (transferType === CheckPropertyErrors.UnknownFormat) {
                fileState.errorMessage = sectionInfo?.fileUploadUnknownFormatErrorMessage ? (
                    <UploadFileException errorMessage={sectionInfo?.fileUploadUnknownFormatErrorMessage} />
                ) : (
                    <FileException>
                        Неподходящее содержимое файла.
                        <br />
                        Загрузите отчет в формате <Underline>{sectionInfo?.name}</Underline>
                    </FileException>
                );
            } else {
                const url: ValidDriveURL = {
                    id: file.id,
                    excelURl: file.driveUri ?? "",
                    xmlURL: excelToXmlUrl,
                    typeId: transferType,
                };
                setValidDriveURLs(value => [...value, url]);
                fileState.typeId = transferType;
            }
        }

        fileState.status = fileState.errorMessage ? UploadFileStatuses.error : UploadFileStatuses.success;
        return fileState;
    }
    const [, setModalErrorBody] = React.useState<string>("");

    const setErrorStep = (errorHeader: string, errorBody: string) => {
        setStep(PatchFufSteps.ErrorMessage);
        setModalHeader(errorHeader);
        setModalErrorBody(errorBody);
    };
    const uploadFilesFromExcel = async (
        pathToTable: string,
        combineMethod: FufCombineMethod,
        isImportByFuf?: boolean
    ) => {
        setLoading(true);
        if (modalSettings?.getTypeId) {
            abortController.current = new AbortController();
            const filteredTransfers: ICombineByTransfers[] = validDriveURLs.reduce(
                (accumulator: ICombineByTransfers[], item: ValidDriveURL) => {
                    accumulator.push({
                        fufDriveUri: item.xmlURL,
                        typeId: item.typeId ?? "",
                    });
                    return accumulator;
                },
                []
            );
            if (filteredTransfers.length) {
                const response = await combineMultipleWithTransfer(filteredTransfers, abortController.current.signal);
                if (abortController.current.signal.aborted) {
                    noop();
                } else if (response?.serverError) {
                    setErrorStep(
                        "Не удалось загрузить файлы",
                        "Повторите попытку через несколько минут. Если ошибка повторяется, напишите в техподдержку."
                    );
                } else {
                    await reload();
                    await delay(3000); // чтобы рывков на интерфейсе при перезагрузке не было
                    onClose();
                }
            }
        } else {
            const validXmlDriveURLs: string[] = validDriveURLs.map(item => item.xmlURL);
            const combinatorType = modalSettings?.combinatorType ?? CombinatorType.Default;
            const response = await importXmls(
                validXmlDriveURLs,
                combineMethod,
                combinatorType,
                isImportByFuf ?? combinatorType !== CombinatorType.Default,
                combinatorType === CombinatorType.Default ? [] : [pathToTable]
            );

            if (response !== undefined) {
                if (response.addedItemsCount !== 0 || response.updatedItemsCount !== 0) {
                    await reload();
                    onClose();
                    return;
                } else {
                    onClose();
                    Toast.push("В файле нет данных для загрузки");
                }
            } else {
                setStep(PatchFufSteps.SelectFiles);
                Toast.push("Не удалось загрузить файл", {
                    label: "Повторить",
                    handler: refreshExcelUpload,
                });
            }
        }
        setLoading(false);
    };

    const getDriveUrls = () =>
        uploadedFiles
            .map(file => (file.status !== UploadFileStatuses.error ? file.driveUri : undefined))
            .filter(isNotNullOrEmpty);

    const extractValidFilesDriveUrls = (): string[] => {
        const filesToUpload = uploadedFiles
            .map(file => (file.status === UploadFileStatuses.success ? file.driveUri : undefined))
            .filter(isNotNullOrEmpty);
        return filesToUpload;
    };

    const setCombineMethodAndUploadFiles = async (combineMethod: FufCombineMethod) => {
        setStep(PatchFufSteps.SelectFiles);
        setFufCombineMethod(combineMethod);
        await addUserFilesToDrive(modalSettings?.multiple);
    };

    const clickOnReplaceButton = async () => {
        track(EXCEL_UPLOAD_SELECT_REPLACE, { path: modalSettings?.pathToTable });
        await setCombineMethodAndUploadFiles(getReplaceMethod());
    };

    const clickOnAddButton = async () => {
        track(EXCEL_UPLOAD_SELECT_ADD, { path: modalSettings?.pathToTable });
        await setCombineMethodAndUploadFiles(modalSettings?.combineType ?? FufCombineMethod.AddToEnd);
    };

    const onApplyUploadFiles = async () => {
        await setCombineMethodAndUploadFiles(getReplaceMethod());
        setStep(PatchFufSteps.SelectFiles);
    };

    const getReplaceMethod = (): FufCombineMethod =>
        modalSettings?.combinatorType === CombinatorType.XmlCombinatorByPath
            ? FufCombineMethod.ReplaceThenAddToEnd
            : FufCombineMethod.ReplaceThenMerge;

    const isSomeFileLoading = uploadedFiles.some(
        file => file.status === UploadFileStatuses.new || file.status === UploadFileStatuses.progress
    );

    const isButtonLoadDisabled =
        loading ||
        !Boolean(getDriveUrls().length) ||
        isSomeFileLoading ||
        (modalSettings?.multiple == false && getDriveUrls().length > 1);

    const isHaveValidAndInvalidFile =
        uploadedFiles.some(item => item.status === UploadFileStatuses.error) &&
        uploadedFiles.some(item => item.status === UploadFileStatuses.success);

    const isStaticAndSelectFufMethod =
        step === PatchFufSteps.SelectCombineFufMethod && modalSettings?.tableType === TableType.static;

    const isDynamicAndSelectFufMethod =
        step === PatchFufSteps.SelectCombineFufMethod && modalSettings?.tableType === TableType.dynamic;

    const confirmMerge = async () => {
        setStep(PatchFufSteps.SelectFiles);
        await setCombineMethodAndUploadFiles(modalSettings?.combineType ?? FufCombineMethod.AddToEnd);
    };

    if (
        step === PatchFufSteps.SelectCombineFufMethod &&
        (modalSettings?.isOnlyAddToEnd || modalSettings?.isOnlyReplace)
    ) {
        return (
            <MergeNotice
                onClose={onClose}
                onSubmit={confirmMerge}
                headerText={"Загрузить таблицу из ЕФС-1?"}
                bodyText={`Новые данные ${
                    modalSettings?.isOnlyReplace ? "заменят данные в разделе" : `будут объединены с\u00A0текущими`
                }`}
            />
        );
    }

    return (
        <Modal
            width={isStaticAndSelectFufMethod ? smallModalWidth : wideModalWidth}
            ignoreBackgroundClick
            onClose={onClose}
            noClose={step === PatchFufSteps.LoadToForm}>
            <Modal.Header>{modalHeader}</Modal.Header>
            <Modal.Body>
                <ModalLoader loading={loading}>
                    <>
                        <StepPage visible={isStaticAndSelectFufMethod}>
                            <ModalText>Новые данные заменят данные в таблице</ModalText>
                        </StepPage>
                        <StepPage visible={isDynamicAndSelectFufMethod}>
                            <CombineMethodPicker
                                onReplaceClick={clickOnReplaceButton}
                                onMergeClick={
                                    modalSettings?.combineType === FufCombineMethod.Merge ? clickOnAddButton : undefined
                                }
                                onAddToEndClick={
                                    modalSettings?.combineType === FufCombineMethod.AddToEnd ||
                                    modalSettings?.combineType === undefined
                                        ? clickOnAddButton
                                        : undefined
                                }
                            />
                        </StepPage>
                        <StepPage visible={step === PatchFufSteps.SelectFiles}>{filesContainer}</StepPage>
                    </>
                </ModalLoader>
            </Modal.Body>
            <StepPage visible={isStaticAndSelectFufMethod || step !== PatchFufSteps.SelectCombineFufMethod}>
                <Modal.Footer panel sticky>
                    <StepPage visible={isStaticAndSelectFufMethod}>
                        <Gapped gap={16}>
                            <Button onClick={onApplyUploadFiles} use="primary" size="medium">
                                Загрузить
                            </Button>
                            <Button onClick={onClose} size="medium">
                                Отменить
                            </Button>
                        </Gapped>
                    </StepPage>
                    <StepPage visible={step !== PatchFufSteps.SelectCombineFufMethod}>
                        <Gapped gap={16}>
                            <Button
                                data-tid={"UploadButton"}
                                disabled={isButtonLoadDisabled}
                                onClick={onDownloadFileHandler}
                                use="primary"
                                size="medium">
                                Загрузить файлы
                                {isHaveValidAndInvalidFile && !isButtonLoadDisabled && " без ошибок"}
                            </Button>
                            {!loading && (
                                <Button data-tid={"CloseModalButton"} onClick={onClose} size="medium">
                                    Закрыть
                                </Button>
                            )}
                        </Gapped>
                    </StepPage>
                </Modal.Footer>
            </StepPage>
        </Modal>
    );
};
